import type { AdminPrivileges } from '../types/adminPrivilegeTypes';

type PrivKey = keyof AdminPrivileges;

// Mirrors backend ADMIN_PRIVILEGE_DEPENDENCY_RULES in lib/authorization/types/admin_privilege.types.ts.
// Each pair [field, requires] means: "field requires requires to also be enabled".
// Keep in sync with backend when rules change.
const DEPENDENCY_RULES: Array<[PrivKey, PrivKey]> = [
  // Admin User Management
  ['adminUserView', 'adminUserList'],
  ['adminUserCreate', 'adminUserList'],
  ['adminUserCreate', 'adminUserView'],
  ['adminUserEdit', 'adminUserList'],
  ['adminUserEdit', 'adminUserView'],
  ['adminUserActivateDeactivate', 'adminUserList'],

  // Subscription Management
  ['subscriptionView', 'subscriptionList'],
  ['subscriptionView', 'serviceTypeList'],
  ['subscriptionView', 'serviceTypeView'],
  ['subscriptionCreate', 'subscriptionList'],
  ['subscriptionCreate', 'subscriptionView'],
  ['subscriptionCreate', 'offerList'],
  ['subscriptionCreate', 'offerView'],
  ['subscriptionCreate', 'serviceTypeList'],
  ['subscriptionEdit', 'subscriptionList'],
  ['subscriptionEdit', 'subscriptionView'],
  ['subscriptionEdit', 'offerList'],
  ['subscriptionEdit', 'offerView'],
  ['subscriptionEdit', 'serviceTypeList'],
  ['subscriptionActivateDeactivate', 'subscriptionList'],
  // Transitive dependencies — Subscription Management
  // subscriptionCreate → subscriptionView → serviceTypeView        (2 hops)
  // subscriptionCreate → subscriptionView → subscriptionList       (already explicit)
  // subscriptionCreate → offerView        → offerList              (2 hops)
  //                                       → subscriptionList       (3 hops, already explicit)
  // subscriptionEdit — identical transitive chains to subscriptionCreate

  // Offer Management
  ['offerList', 'subscriptionList'],
  ['offerView', 'offerList'],
  ['offerCreate', 'offerList'],
  ['offerCreate', 'offerView'],
  ['offerCreate', 'subscriptionCreate'],
  ['offerCreate', 'subscriptionList'],
  ['offerEdit', 'offerList'],
  ['offerEdit', 'offerView'],
  ['offerEdit', 'subscriptionCreate'],
  ['offerEdit', 'subscriptionList'],
  ['offerActivateDeactivate', 'offerList'],
  // Transitive dependencies — Offer Management
  // offerView             → offerList → subscriptionList          (2 hops)
  // offerCreate           → subscriptionCreate → subscriptionView (2 hops)
  //                                            → serviceTypeList  (2 hops)
  //                                            → serviceTypeView  (3 hops, via subscriptionView)
  // offerEdit             — identical transitive chains to offerCreate
  // offerActivateDeactivate → offerList → subscriptionList        (2 hops)

  // Client Management
  ['clientView', 'clientList'],
  ['clientView', 'facilityList'],
  ['clientView', 'facilityView'],
  ['clientView', 'equipmentList'],
  ['clientView', 'equipmentView'],
  ['clientView', 'facilityUserList'],
  ['clientView', 'facilityUserView'],
  ['clientCreate', 'clientList'],
  ['clientCreate', 'clientView'],
  ['clientCreate', 'subscriptionList'],
  ['clientCreate', 'subscriptionView'],
  ['clientCreate', 'facilityCreate'],
  ['clientCreate', 'facilityList'],
  ['clientCreate', 'facilityEdit'],
  ['clientCreate', 'facilityActivateDeactivate'],
  ['clientCreate', 'equipmentCreate'],
  ['clientCreate', 'equipmentList'],
  ['clientCreate', 'equipmentEdit'],
  // Transitive dependencies — Client Management (clientView)
  // clientView has no transitive deps on check — all 7 are direct.
  // Uncheck cascade — clientView (large — clientView is a cross-module requirement)
  // Unchecking clientView auto-unchecks everything that requires it (all 1 hop):
  // clientCreate, clientUpdate, facilityView, facilityCreate, facilityEdit,
  // facilityActivateDeactivate, equipmentView, equipmentCreate, equipmentEdit,
  // facilityUserList, facilityUserView
  // Transitive dependencies — Client Management (clientCreate)
  // clientCreate → clientView        → facilityView              (2 hops)
  //                                  → equipmentView             (2 hops)
  //                                  → facilityUserList          (2 hops)
  //                                  → facilityUserView          (2 hops)
  // clientCreate → subscriptionView  → serviceTypeList           (2 hops)
  //                                  → serviceTypeView           (2 hops)
  // clientCreate → facilityEdit      → clientUpdate              (2 hops)
  // clientCreate → equipmentEdit     → clientUpdate              (2 hops, same result)
  // Uncheck cascade — clientCreate
  // facilityCreate and equipmentCreate are auto-unchecked because they list
  // clientCreate as a direct requirement. Nothing else cascades further.
  // Transitive dependencies — Client Management (clientUpdate)
  // clientUpdate → clientView        → facilityView              (2 hops)
  //                                  → equipmentView             (2 hops)
  //                                  → facilityUserList          (2 hops)
  //                                  → facilityUserView          (2 hops)
  // clientUpdate → subscriptionView  → serviceTypeList           (2 hops)
  //                                  → serviceTypeView           (2 hops)
  // serviceTypeList/View are NOT auto-unchecked when clientUpdate is unchecked
  // (they were transitive on check; uncheck cascade runs reverse-dep direction only)
  // Uncheck cascade — clientUpdate
  // facilityEdit and equipmentEdit are auto-unchecked (they require clientUpdate)
  // → clientCreate is then auto-unchecked (it requires facilityEdit/equipmentEdit)
  // → facilityCreate and equipmentCreate follow (they require clientCreate,
  //   and are also direct deps of clientUpdate so reached in 1 hop too)
  ['clientUpdate', 'clientList'],
  ['clientUpdate', 'clientView'],
  ['clientUpdate', 'subscriptionList'],
  ['clientUpdate', 'subscriptionView'],
  ['clientUpdate', 'facilityCreate'],
  ['clientUpdate', 'facilityEdit'],
  ['clientUpdate', 'facilityList'],
  ['clientUpdate', 'facilityActivateDeactivate'],
  ['clientUpdate', 'equipmentCreate'],
  ['clientUpdate', 'equipmentEdit'],
  ['clientUpdate', 'equipmentList'],
  ['clientActivateDeactivate', 'clientList'],

  // QMS Document Category Management
  ['qmsDocCategoryView', 'qmsDocCategoryList'],
  ['qmsDocCategoryCreate', 'qmsDocCategoryList'],
  ['qmsDocCategoryCreate', 'qmsDocCategoryView'],
  ['qmsDocCategoryEdit', 'qmsDocCategoryList'],
  ['qmsDocCategoryEdit', 'qmsDocCategoryView'],
  ['qmsDocCategoryActivateDeactivate', 'qmsDocCategoryList'],

  // QMS Document Template Management
  ['qmsDocTemplateView', 'qmsDocTemplateList'],
  ['qmsDocTemplateCreate', 'qmsDocTemplateList'],
  ['qmsDocTemplateCreate', 'qmsDocTemplateView'],
  ['qmsDocTemplateEdit', 'qmsDocTemplateList'],
  ['qmsDocTemplateEdit', 'qmsDocTemplateView'],
  ['qmsDocTemplateActivateDeactivate', 'qmsDocTemplateList'],

  // QMS Document Update Requests
  ['qmsUpdateRequestView', 'qmsUpdateRequestList'],
  ['qmsUpdateRequestEdit', 'qmsUpdateRequestList'],
  ['qmsUpdateRequestEdit', 'qmsUpdateRequestView'],
  ['qmsUpdateRequestUpdateStatus', 'qmsUpdateRequestList'],
  ['qmsUpdateRequestUpdateStatus', 'qmsUpdateRequestView'],

  // Service Type Management
  ['serviceTypeView', 'serviceTypeList'],
  ['serviceTypeCreate', 'serviceTypeList'],
  ['serviceTypeCreate', 'serviceTypeView'],
  ['serviceTypeEdit', 'serviceTypeList'],
  ['serviceTypeEdit', 'serviceTypeView'],
  ['serviceTypeActivateDeactivate', 'serviceTypeList'],

  // Technician Management
  ['technicianView', 'technicianList'],
  ['technicianCreate', 'technicianList'],
  ['technicianCreate', 'technicianView'],
  ['technicianEdit', 'technicianList'],
  ['technicianEdit', 'technicianView'],
  ['technicianActivateDeactivate', 'technicianList'],

  // Invoice Template Management
  ['invoiceTemplateView', 'invoiceTemplateList'],
  ['invoiceTemplateCreate', 'invoiceTemplateList'],
  ['invoiceTemplateCreate', 'invoiceTemplateView'],
  ['invoiceTemplateEdit', 'invoiceTemplateList'],
  ['invoiceTemplateEdit', 'invoiceTemplateView'],
  ['invoiceTemplateActivateDeactivate', 'invoiceTemplateList'],

  // Certificate Template Management
  ['certificateTemplateView', 'certificateTemplateList'],
  ['certificateTemplateCreate', 'certificateTemplateList'],
  ['certificateTemplateCreate', 'certificateTemplateView'],
  ['certificateTemplateCreate', 'formTemplateView'],
  ['certificateTemplateCreate', 'formTemplateList'],
  ['certificateTemplateEdit', 'certificateTemplateList'],
  ['certificateTemplateEdit', 'certificateTemplateView'],
  ['certificateTemplateEdit', 'formTemplateView'],
  ['certificateTemplateEdit', 'formTemplateList'],

  // Form Template Management
  ['formTemplateView', 'formTemplateList'],
  ['formTemplateCreate', 'formTemplateList'],
  ['formTemplateCreate', 'formTemplateView'],
  ['formTemplateEdit', 'formTemplateList'],
  ['formTemplateEdit', 'formTemplateView'],

  // Facility Management
  ['facilityView', 'facilityList'],
  ['facilityView', 'clientList'],
  ['facilityView', 'clientView'],
  ['facilityCreate', 'facilityList'],
  ['facilityCreate', 'clientCreate'],
  ['facilityCreate', 'clientList'],
  ['facilityCreate', 'clientView'],
  ['facilityCreate', 'equipmentCreate'],
  ['facilityCreate', 'equipmentList'],
  ['facilityEdit', 'facilityList'],
  ['facilityEdit', 'facilityView'],
  ['facilityEdit', 'facilityUserView'],
  ['facilityEdit', 'clientList'],
  ['facilityEdit', 'clientView'],
  ['facilityEdit', 'clientUpdate'],
  ['facilityEdit', 'equipmentList'],
  ['facilityEdit', 'equipmentEdit'],
  ['facilityActivateDeactivate', 'facilityList'],
  ['facilityActivateDeactivate', 'clientList'],
  ['facilityActivateDeactivate', 'clientView'],
  ['facilityList', 'clientList'],
  ['facilityList', 'clientView'],
  // Transitive dependencies — Facility Management (facilityView)
  // facilityView → clientView → equipmentList                     (2 hops)
  //                           → equipmentView                     (2 hops)
  //                           → facilityUserList                  (2 hops)
  //                           → facilityUserView                  (2 hops)
  // Uncheck cascade — facilityView
  // facilityEdit, equipmentList are auto-unchecked (they require facilityView directly)
  // clientView is auto-unchecked (clientView requires facilityView)
  // → clientView uncheck cascades to everything that requires it (large)
  //
  // Transitive dependencies — Facility Management (facilityCreate)
  // facilityCreate → clientCreate → clientView  → facilityView    (3 hops)
  //                                             → equipmentView   (3 hops)
  //                                             → facilityUserList (3 hops)
  //                                             → facilityUserView (3 hops)
  // facilityCreate → clientCreate → facilityEdit → clientUpdate   (3 hops)
  // facilityCreate → clientView   → facilityView                  (2 hops)
  //                               → equipmentView                 (2 hops)
  //                               → facilityUserList              (2 hops)
  //                               → facilityUserView              (2 hops)
  // Uncheck cascade — facilityCreate
  // clientCreate, equipmentCreate, clientUpdate are auto-unchecked (they require facilityCreate)
  // → clientUpdate → facilityEdit, equipmentEdit follow
  //
  // Transitive dependencies — Facility Management (facilityEdit)
  // facilityEdit → facilityUserView → facilityUserList            (2 hops)
  // facilityEdit → clientView       → equipmentView               (2 hops)
  // facilityEdit → clientUpdate     → subscriptionList/View       (2 hops)
  //                                 → facilityCreate              (2 hops)
  //                                 → facilityActivateDeactivate  (2 hops)
  //                                 → equipmentCreate             (2 hops)
  //                                 → subscriptionView → serviceTypeList/View (3 hops)
  //                                 → facilityCreate   → clientCreate         (3 hops)
  // Uncheck cascade — facilityEdit
  // clientCreate, clientUpdate, equipmentEdit are auto-unchecked (they require facilityEdit)
  // → clientCreate → facilityCreate, equipmentCreate follow
  //
  // Transitive dependencies — Facility Management (facilityActivateDeactivate)
  // facilityActivateDeactivate → clientView → facilityView        (2 hops)
  //                                         → equipmentList       (2 hops)
  //                                         → equipmentView       (2 hops)
  //                                         → facilityUserList    (2 hops)
  //                                         → facilityUserView    (2 hops)
  // Uncheck cascade — facilityActivateDeactivate
  // clientCreate and clientUpdate are auto-unchecked (they require facilityActivateDeactivate)
  //
  // Transitive dependencies — Facility Management (facilityList)
  // facilityList → clientView → facilityView                      (2 hops)
  //                           → equipmentList                     (2 hops)
  //                           → equipmentView                     (2 hops)
  //                           → facilityUserList                  (2 hops)
  //                           → facilityUserView                  (2 hops)
  // Uncheck cascade — facilityList (large — foundational permission)
  // Almost every Facility/Equipment/FacilityUser privilege requires facilityList directly.
  // clientView also requires facilityList, so unchecking facilityList triggers the
  // full clientView uncheck cascade in addition to all direct revdeps.

  // Equipment Management
  ['equipmentList', 'facilityList'],
  ['equipmentList', 'facilityView'],
  ['equipmentList', 'clientList'],
  ['equipmentList', 'clientView'],
  ['equipmentView', 'equipmentList'],
  ['equipmentView', 'facilityList'],
  ['equipmentView', 'clientList'],
  ['equipmentView', 'clientView'],
  ['equipmentCreate', 'equipmentList'],
  ['equipmentCreate', 'facilityCreate'],
  ['equipmentCreate', 'facilityList'],
  ['equipmentCreate', 'clientCreate'],
  ['equipmentCreate', 'clientList'],
  ['equipmentCreate', 'clientView'],
  ['equipmentEdit', 'equipmentList'],
  ['equipmentEdit', 'equipmentView'],
  ['equipmentEdit', 'facilityList'],
  ['equipmentEdit', 'facilityEdit'],
  ['equipmentEdit', 'clientList'],
  ['equipmentEdit', 'clientView'],
  ['equipmentEdit', 'clientUpdate'],
  // Transitive dependencies — Equipment Management (equipmentList)
  // equipmentList → facilityView → facilityList                   (2 hops, also direct)
  //                              → clientList                     (2 hops, also direct)
  //                              → clientView                     (2 hops, also direct)
  // equipmentList → clientView   → facilityUserList               (2 hops)
  //                              → facilityUserView               (2 hops)
  // Uncheck cascade — equipmentList (large — foundational permission)
  // clientView requires equipmentList, so unchecking equipmentList triggers the full
  // clientView uncheck cascade. Direct revdeps also include: equipmentView, equipmentCreate,
  // equipmentEdit, facilityEdit, facilityCreate, clientCreate, clientUpdate.
  //
  // Transitive dependencies — Equipment Management (equipmentView)
  // equipmentView → clientView → facilityView                     (2 hops)
  //                            → facilityUserList                 (2 hops)
  //                            → facilityUserView                 (2 hops)
  // Uncheck cascade — equipmentView
  // equipmentEdit and clientView are auto-unchecked (they require equipmentView)
  // → clientView uncheck cascades to everything that requires it
  //
  // Transitive dependencies — Equipment Management (equipmentCreate)
  // equipmentCreate → facilityCreate → clientCreate               (2 hops)
  // equipmentCreate → clientCreate  → clientView → facilityView   (3 hops)
  //                                             → equipmentView   (3 hops)
  //                                             → facilityUserList (3 hops)
  //                                             → facilityUserView (3 hops)
  // equipmentCreate → clientCreate  → facilityEdit → clientUpdate (3 hops)
  // equipmentCreate → clientView    → facilityView                (2 hops)
  //                                 → equipmentView               (2 hops)
  //                                 → facilityUserList            (2 hops)
  //                                 → facilityUserView            (2 hops)
  // Uncheck cascade — equipmentCreate
  // facilityCreate, clientCreate, clientUpdate are auto-unchecked (they require equipmentCreate)
  // → clientUpdate → facilityEdit, equipmentEdit follow
  //
  // Transitive dependencies — Equipment Management (equipmentEdit)
  // equipmentEdit → equipmentView → clientView → facilityView     (3 hops)
  //                                            → facilityUserList (3 hops)
  //                                            → facilityUserView (3 hops)
  // equipmentEdit → facilityEdit  → facilityUserView              (2 hops)
  //                               → facilityUserList              (3 hops, via facilityUserView)
  // equipmentEdit → clientUpdate  → subscriptionList/View         (2 hops)
  //                               → facilityCreate                (2 hops)
  //                               → facilityActivateDeactivate    (2 hops)
  //                               → equipmentCreate               (2 hops)
  //                               → subscriptionView → serviceTypeList/View (3 hops)
  // Uncheck cascade — equipmentEdit
  // clientCreate, clientUpdate, facilityEdit are auto-unchecked (they require equipmentEdit)
  // → facilityCreate, equipmentCreate follow (they require clientCreate/clientUpdate)

  // Facility User Management
  ['facilityUserList', 'facilityList'],
  ['facilityUserList', 'clientList'],
  ['facilityUserList', 'clientView'],
  ['facilityUserView', 'facilityUserList'],
  ['facilityUserView', 'facilityList'],
  ['facilityUserView', 'clientList'],
  ['facilityUserView', 'clientView'],
  // Transitive dependencies — Facility User Management (facilityUserList)
  // facilityUserList → clientView → facilityView                  (2 hops)
  //                               → equipmentList                 (2 hops)
  //                               → equipmentView                 (2 hops)
  //                               → facilityUserView              (2 hops)
  // Uncheck cascade — facilityUserList
  // facilityUserView and clientView are auto-unchecked (they require facilityUserList)
  // → clientView uncheck cascades to everything that requires it
  //
  // Transitive dependencies — Facility User Management (facilityUserView)
  // facilityUserView → facilityUserList → clientView → facilityView (3 hops)
  //                                                  → equipmentList (3 hops)
  //                                                  → equipmentView (3 hops)
  // facilityUserView → clientView       → facilityView              (2 hops)
  //                                     → equipmentList             (2 hops)
  //                                     → equipmentView             (2 hops)
  // Uncheck cascade — facilityUserView
  // facilityEdit and clientView are auto-unchecked (they require facilityUserView)
  // → facilityEdit → clientCreate, clientUpdate, equipmentEdit follow

  // Service Request Management
  ['serviceRequestView', 'serviceRequestList'],
  ['serviceRequestAssign', 'serviceRequestList'],
  ['serviceRequestAssign', 'serviceRequestView'],
  ['serviceRequestAssign', 'formTemplateCreate'],
  ['serviceRequestAssign', 'formTemplateView'],
  ['serviceRequestAssign', 'formTemplateList'],
  ['serviceRequestAssign', 'certificateTemplateCreate'],
  ['serviceRequestAssign', 'certificateTemplateView'],
  ['serviceRequestAssign', 'certificateTemplateList'],
  ['serviceRequestAssign', 'technicianList'],
  ['serviceRequestAssign', 'invoiceTemplateCreate'],
  ['serviceRequestAssign', 'invoiceTemplateList'],
  ['serviceRequestAssign', 'invoiceTemplateView'],
  ['serviceRequestUpdateStatus', 'serviceRequestList'],
  ['serviceRequestUpdateStatus', 'serviceRequestView'],
  ['serviceRequestAcceptReject', 'serviceRequestList'],
  ['serviceRequestAcceptReject', 'serviceRequestView'],
  ['serviceRequestReschedule', 'serviceRequestList'],
  ['serviceRequestReschedule', 'serviceRequestView'],
  ['serviceRequestReschedule', 'formTemplateCreate'],
  ['serviceRequestReschedule', 'formTemplateView'],
  ['serviceRequestReschedule', 'formTemplateList'],
  ['serviceRequestReschedule', 'certificateTemplateCreate'],
  ['serviceRequestReschedule', 'certificateTemplateView'],
  ['serviceRequestReschedule', 'certificateTemplateList'],
  ['serviceRequestReschedule', 'technicianList'],
  ['serviceRequestReschedule', 'invoiceTemplateCreate'],
  ['serviceRequestReschedule', 'invoiceTemplateList'],
  ['serviceRequestReschedule', 'invoiceTemplateView'],

  // Contact Request Management
  ['contactRequestView', 'contactRequestList'],
  ['contactRequestAssign', 'contactRequestList'],
  ['contactRequestAssign', 'contactRequestView'],
  ['contactRequestReschedule', 'contactRequestList'],
  ['contactRequestReschedule', 'contactRequestView'],
  ['contactRequestUpdateStatus', 'contactRequestList'],
  ['contactRequestUpdateStatus', 'contactRequestView'],
  ['contactRequestAcceptReject', 'contactRequestList'],
  ['contactRequestAcceptReject', 'contactRequestView'],

  // Certificate Management
  ['certificateList', 'technicianList'],
  ['certificateList', 'technicianView'],
  ['certificateList', 'equipmentList'],
  ['certificateView', 'certificateList'],
  ['certificateDownload', 'certificateList'],
  ['certificateDownload', 'certificateView'],

  // Invoice Management
  // Check transitive chains:
  //   invoicePublish → invoiceView    → invoiceList  (TRUE* via invoiceView)
  //   invoicePublish → invoiceDownload → invoiceList (TRUE* via invoiceDownload)
  //   invoicePublish → invoiceDownload → invoiceView (TRUE* via invoiceDownload)
  ['invoiceDraftView', 'invoiceDraftList'],
  ['invoiceDraftEdit', 'invoiceDraftList'],
  ['invoiceDraftEdit', 'invoiceDraftView'],
  ['invoicePublish', 'invoiceDraftList'],
  ['invoicePublish', 'invoiceDraftView'],
  ['invoicePublish', 'invoiceView'],
  ['invoicePublish', 'invoiceDownload'],
  ['invoiceView', 'invoiceList'],
  ['invoiceDownload', 'invoiceList'],
  ['invoiceDownload', 'invoiceView'],
];

// Computed at module load time from DEPENDENCY_RULES.
// CHECK_DEPS[field]    = keys that must be enabled  when field is enabled.
// UNCHECK_DEPS[field]  = keys that must be disabled when field is disabled.
//
// Sync loop over a static array — NOT the async batch pattern used on the backend
// (Array.from + reduce + Promise.allSettled). That pattern is for concurrent I/O
// (AVP API calls). This is pure in-memory map building at module init; a for..of
// is the correct tool here.
const CHECK_DEPS = new Map<PrivKey, PrivKey[]>();
const UNCHECK_DEPS = new Map<PrivKey, PrivKey[]>();

for (const [field, requires] of DEPENDENCY_RULES) {
  let checkList = CHECK_DEPS.get(field);
  if (!checkList) {
    checkList = [];
    CHECK_DEPS.set(field, checkList);
  }
  checkList.push(requires);

  let uncheckList = UNCHECK_DEPS.get(requires);
  if (!uncheckList) {
    uncheckList = [];
    UNCHECK_DEPS.set(requires, uncheckList);
  }
  uncheckList.push(field);
}

// BFS graph traversal — propagates cascades transitively across modules.
// Example: uncheck clientList → uncheck clientView → uncheck facilityView (two hops).
// A single-level forEach would miss the second hop.
//
// The while loop is intentional: this is a standard BFS queue drain, not async work.
// Promise/allSettled is irrelevant here — there is no I/O, only in-memory state.
// The visited Set terminates circular pairs (clientCreate ↔ facilityCreate) that exist in the dependency rules.
export function applyAdminPrivilegeCascade(
  current: AdminPrivileges,
  key: PrivKey,
  value: boolean
): AdminPrivileges {
  const next = { ...current };
  const depMap = value ? CHECK_DEPS : UNCHECK_DEPS;
  const queue: PrivKey[] = [key];
  const visited = new Set<PrivKey>();

  while (queue.length > 0) {
    const curr = queue.shift();
    if (curr === undefined) {
      break;
    }
    if (visited.has(curr)) {
      continue;
    } // handles circular deps — do not remove
    visited.add(curr);
    next[curr] = value;
    depMap.get(curr)?.forEach((dep) => {
      if (!visited.has(dep)) {
        queue.push(dep);
      }
    });
  }

  return next;
}
